import { Injectable, inject } from '@angular/core';
import { SecureStorage } from '@aparajita/capacitor-secure-storage';
import { PlatformService } from './platform.service';
import { UserProfile } from '../models/user-profile.model';

/**
 * TokenStorageService
 *
 * Securely stores OAuth tokens using platform-appropriate storage mechanisms:
 * - iOS: Keychain (via SecureStoragePlugin)
 * - Android: KeyStore (via SecureStoragePlugin)
 * - Web: sessionStorage (fallback with warning)
 *
 * This service ensures tokens are stored securely on mobile platforms
 * and never persisted in plaintext.
 */
@Injectable({
  providedIn: 'root'
})
export class TokenStorageService {
  private platformService = inject(PlatformService);

  private readonly KEYS = {
    ACCESS_TOKEN: 'access_token',
    REFRESH_TOKEN: 'refresh_token',
    ID_TOKEN: 'id_token',
    USER_PROFILE: 'user_profile',
    TOKEN_EXPIRY: 'token_expiry'
  };

  // SECURITY FIX (HIGH-01): In-memory storage for web platform to prevent XSS token theft
  // Tokens will be lost on page refresh, requiring re-authentication
  private webTokenCache: Map<string, string> = new Map();
  private webStorageWarningShown = false;

  /**
   * Store access token
   */
  async setAccessToken(token: string): Promise<void> {
    await this.setItem(this.KEYS.ACCESS_TOKEN, token);
  }

  /**
   * Retrieve access token
   */
  async getAccessToken(): Promise<string | null> {
    return await this.getItem(this.KEYS.ACCESS_TOKEN);
  }

  /**
   * Store refresh token
   */
  async setRefreshToken(token: string): Promise<void> {
    await this.setItem(this.KEYS.REFRESH_TOKEN, token);
  }

  /**
   * Retrieve refresh token
   */
  async getRefreshToken(): Promise<string | null> {
    return await this.getItem(this.KEYS.REFRESH_TOKEN);
  }

  /**
   * Store ID token
   */
  async setIdToken(token: string): Promise<void> {
    await this.setItem(this.KEYS.ID_TOKEN, token);
  }

  /**
   * Retrieve ID token
   */
  async getIdToken(): Promise<string | null> {
    return await this.getItem(this.KEYS.ID_TOKEN);
  }

  /**
   * Store token expiry time (Unix timestamp in milliseconds)
   */
  async setTokenExpiry(expiry: number): Promise<void> {
    await this.setItem(this.KEYS.TOKEN_EXPIRY, expiry.toString());
  }

  /**
   * Retrieve token expiry time
   */
  async getTokenExpiry(): Promise<number | null> {
    const expiry = await this.getItem(this.KEYS.TOKEN_EXPIRY);
    return expiry ? parseInt(expiry, 10) : null;
  }

  /**
   * Store user profile
   */
  async setUserProfile(profile: UserProfile): Promise<void> {
    await this.setItem(this.KEYS.USER_PROFILE, JSON.stringify(profile));
  }

  /**
   * Retrieve user profile
   */
  async getUserProfile(): Promise<UserProfile | null> {
    const profile = await this.getItem(this.KEYS.USER_PROFILE);
    return profile ? JSON.parse(profile) : null;
  }

  /**
   * Clear all stored data (tokens and user profile)
   */
  async clearAll(): Promise<void> {
    await this.clearTokens();
    await this.removeItem(this.KEYS.USER_PROFILE);
  }

  /**
   * Clear only token data (preserves user profile)
   */
  async clearTokens(): Promise<void> {
    await Promise.all([
      this.removeItem(this.KEYS.ACCESS_TOKEN),
      this.removeItem(this.KEYS.REFRESH_TOKEN),
      this.removeItem(this.KEYS.ID_TOKEN),
      this.removeItem(this.KEYS.TOKEN_EXPIRY)
    ]);
  }

  /**
   * Generic set method for storing arbitrary key-value pairs
   * Used for PKCE parameters and OAuth state
   */
  async set(key: string, value: string): Promise<void> {
    await this.setItem(key, value);
  }

  /**
   * Generic get method for retrieving arbitrary key-value pairs
   * Used for PKCE parameters and OAuth state
   */
  async get(key: string): Promise<string | null> {
    return await this.getItem(key);
  }

  /**
   * Generic remove method for deleting arbitrary keys
   * Used for cleaning up PKCE parameters and OAuth state
   */
  async remove(key: string): Promise<void> {
    await this.removeItem(key);
  }

  /**
   * Store a key-value pair using platform-appropriate storage
   *
   * @private
   */
  private async setItem(key: string, value: string): Promise<void> {
    if (this.platformService.isMobile()) {
      // Use secure storage on mobile (iOS Keychain / Android KeyStore)
      try {
        await SecureStorage.set(key, value);
      } catch (error) {
        console.error('Secure storage set failed:', error);
        throw error;
      }
    } else {
      // SECURITY FIX (HIGH-01): Use in-memory storage on web to prevent XSS attacks
      // Tokens are NOT persisted and will be lost on page refresh
      // This is more secure than sessionStorage which is vulnerable to XSS
      this.webTokenCache.set(key, value);
      if (!this.webStorageWarningShown) {
        this.webStorageWarningShown = true;
        console.warn('[TokenStorage] Using in-memory storage on web platform - tokens will be lost on page refresh');
      }
    }
  }

  /**
   * Retrieve a value by key using platform-appropriate storage
   *
   * @private
   */
  private async getItem(key: string): Promise<string | null> {
    if (this.platformService.isMobile()) {
      try {
        const result = await SecureStorage.get(key);
        return result as string || null;
      } catch {
        // Key not found or other error
        return null;
      }
    } else {
      // SECURITY FIX (HIGH-01): Retrieve from in-memory cache
      return this.webTokenCache.get(key) || null;
    }
  }

  /**
   * Remove a key-value pair using platform-appropriate storage
   *
   * @private
   */
  private async removeItem(key: string): Promise<void> {
    if (this.platformService.isMobile()) {
      try {
        await SecureStorage.remove(key);
      } catch {
        // Ignore if key doesn't exist
      }
    } else {
      // SECURITY FIX (HIGH-01): Remove from in-memory cache
      this.webTokenCache.delete(key);
    }
  }
}
